import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useSSE } from '@/hooks/useSSE';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useGuidedTour } from '@/hooks/useGuidedTour';

// Layout
import AppShell from '@/components/layout/AppShell';

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage';
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage';

// Dashboard Pages
import EmployeeDashboard from '@/pages/dashboard/EmployeeDashboard';
import MDDashboard from '@/pages/dashboard/MDDashboard';
import TeamDashboard from '@/pages/dashboard/TeamDashboard';

// Leave Pages
import ApplyLeavePage from '@/pages/leaves/ApplyLeavePage';
import MyLeavesPage from '@/pages/leaves/MyLeavesPage';

// Approval Pages
import ApprovalInboxPage from '@/pages/approvals/ApprovalInboxPage';
import TeamCalendarPage from '@/pages/approvals/TeamCalendarPage';

// Admin Pages
import HierarchyEditorPage from '@/pages/admin/HierarchyEditorPage';
import UserManagementPage from '@/pages/admin/UserManagementPage';
import PolicyConfigPage from '@/pages/admin/PolicyConfigPage';
import LeaveTypesPage from '@/pages/admin/LeaveTypesPage';
import HolidaysPage from '@/pages/admin/HolidaysPage';
import SystemSettingsPage from '@/pages/admin/SystemSettingsPage';

// Reports & Audit
import ReportsPage from '@/pages/admin/ReportsPage';
import AuditLogPage from '@/pages/admin/AuditLogPage';

// Profile
import ProfilePage from '@/pages/profile/ProfilePage';

// ── Protected Route ──────────────────────────────────────────
function ProtectedRoute({ children, roles }: { children: React.ReactNode; roles?: string[] }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (roles && user && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
}

// ── Dashboard redirect by role ───────────────────────────────
function DashboardRedirect() {
  const { user } = useAuthStore();

  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case 'MD': return <Navigate to="/dashboard/md" replace />;
    case 'MANAGER':
    case 'SENIOR_MANAGER': return <Navigate to="/dashboard/team" replace />;
    case 'ORG_ADMIN':
    case 'HR_MANAGER': return <Navigate to="/approvals/team-calendar" replace />;
    default:
      // EMPLOYEE with direct reports → Team Dashboard as primary
      if (user.hasDirectReports) return <Navigate to="/dashboard/team" replace />;
      return <Navigate to="/dashboard/employee" replace />;
  }
}

// ── App with SSE + session timeout + tour ───────────────────
function AppWithProviders() {
  const { isAuthenticated } = useAuthStore();
  useSSE();
  useSessionTimeout();
  useGuidedTour();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        {/* Dashboard redirect */}
        <Route path="/dashboard" element={<DashboardRedirect />} />

        {/* Employee dashboard */}
        <Route path="/dashboard/employee" element={<EmployeeDashboard />} />

        {/* MD dashboard */}
        <Route
          path="/dashboard/md"
          element={
            <ProtectedRoute roles={['MD']}>
              <MDDashboard />
            </ProtectedRoute>
          }
        />

        {/* Team/Manager dashboard — open to any role that has direct reports */}
        <Route
          path="/dashboard/team"
          element={
            <ProtectedRoute roles={['MANAGER', 'SENIOR_MANAGER', 'MD', 'EMPLOYEE']}>
              <TeamDashboard />
            </ProtectedRoute>
          }
        />

        {/* Leave routes */}
        <Route path="/leaves/apply" element={<ApplyLeavePage />} />
        <Route path="/leaves/my" element={<MyLeavesPage />} />

        {/* Approval routes */}
        <Route
          path="/approvals/inbox"
          element={
            <ProtectedRoute roles={['MANAGER', 'SENIOR_MANAGER', 'MD', 'EMPLOYEE']}>
              <ApprovalInboxPage />
            </ProtectedRoute>
          }
        />
        <Route path="/approvals/team-calendar" element={<TeamCalendarPage />} />

        {/* Admin routes */}
        <Route
          path="/admin/users"
          element={<ProtectedRoute roles={['ORG_ADMIN']}><UserManagementPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/policies"
          element={<ProtectedRoute><PolicyConfigPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/leave-types"
          element={<ProtectedRoute roles={['ORG_ADMIN', 'HR_MANAGER']}><LeaveTypesPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/holidays"
          element={<ProtectedRoute roles={['ORG_ADMIN', 'HR_MANAGER']}><HolidaysPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/settings"
          element={<ProtectedRoute roles={['ORG_ADMIN']}><SystemSettingsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/reports"
          element={<ProtectedRoute roles={['ORG_ADMIN', 'HR_MANAGER', 'MD', 'MANAGER', 'SENIOR_MANAGER']}><ReportsPage /></ProtectedRoute>}
        />
        <Route
          path="/admin/audit"
          element={<ProtectedRoute roles={['ORG_ADMIN', 'HR_MANAGER']}><AuditLogPage /></ProtectedRoute>}
        />

        {/* Profile */}
        <Route path="/profile" element={<ProfilePage />} />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppWithProviders />
    </BrowserRouter>
  );
}
